// Ответ с ошибкой: { code, message, nextStep, operation, fields? } (contracts/api.md, «Общие
// правила»; 002 — номер операции).
import { z } from './zod.ts';
import { AuditEntrySchema, ConflictChangesSchema } from '../audit/entry.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { ConflictDetailsSchema } from '../minutes/conflict.ts';
import { fillNamed } from '../text/template.ts';
import { DateSchema, IdSchema, OperationIdSchema, PersonRefSchema } from './common.ts';

export const ApiErrorSchema = z.object({
  code: ErrorCodeSchema,
  message: z.string(),
  nextStep: z.string(),
  /**
   * Номер операции (FR-027): сервер ставит его в каждый ответ с ошибкой, тот же — в заголовке
   * X-KP-Operation. Нет его только у ответа не от сервиса (прокси, обрыв связи).
   */
  operation: OperationIdSchema.optional(),
  /** Ошибки полей формы: { поле: текст с примером } (FR-030). Поле вложенного объекта — через точку. */
  fields: z.record(z.string(), z.string()).optional(),
  /** AUTH-002: секунд до конца блокировки; тот же срок — в заголовке Retry-After. */
  retryAfter: z.number().int().nonnegative().optional(),
  /** REC-008: сколько записей попало бы в выгрузку. */
  count: z.number().int().nonnegative().optional(),
  /**
   * DOC-002 (010, FR-039, FR-040): листов с шапкой, которую не узнала ни одна схема, — окно
   * загрузки по нему предлагает мастер подключения.
   */
  headerSheets: z.number().int().nonnegative().optional(),
  /** REC-003: пользователь, у которого уже эта почта, — его можно открыть или вернуть из архива. */
  existingUserId: IdSchema.optional(),
  /** REC-001: текущая запись. */
  current: z.unknown().optional(),
  /**
   * REC-001: все изменения, поднявшие версию после If-Match, новые сверху, не больше 50; всего их —
   * текущая версия − If-Match (FR-028, SC-004; T124).
   */
  changes: ConflictChangesSchema.optional(),
  /** REC-001: последнее изменение записи — кто, когда, было, стало; первое из changes. */
  lastChange: AuditEntrySchema.nullable().optional(),
  /**
   * REC-001 команды таблицы протокола (011, ADR-0032): ревизия сервера и конфликты — поле или
   * дерево, база, текущее и пришедшее значение, кто и когда изменил.
   */
  conflict: ConflictDetailsSchema.optional(),
  /** REC-005: замещение той же роли, с которым пересечение по датам, — заместитель, с и по. */
  overlap: z
    .object({
      id: IdSchema,
      deputy: PersonRefSchema,
      validFrom: DateSchema,
      validTo: DateSchema,
    })
    .optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

/**
 * Ответ сервиса с ошибкой — так его описывает OpenAPI (contracts/api.md, «Общие правила»): номер
 * операции есть всегда (FR-027). Клиент разбирает ответ по ApiErrorSchema, где номер необязателен:
 * ошибку мог вернуть не сервис, а прокси или обрыв связи.
 */
export const ApiErrorResponseSchema = ApiErrorSchema.extend({ operation: OperationIdSchema });

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

/** 409 REC-001 с текущей записью нужного вида. */
export function conflictErrorSchema<T extends z.ZodType>(record: T) {
  return ApiErrorSchema.extend({
    code: z.literal('REC-001'),
    current: record,
    changes: ConflictChangesSchema,
    lastChange: AuditEntrySchema.nullable(),
  });
}

interface IssueLike {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}

/**
 * Ошибки проверки схемы → поле fields ответа REC-007: первая ошибка каждого поля.
 * vars заполняет метки в текстах, например { домен: 'example.ru' }.
 */
export function fieldErrorsFromIssues(
  issues: readonly IssueLike[],
  vars: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of issues) {
    const key = issue.path.map(String).join('.');
    if (!(key in result)) result[key] = fillNamed(issue.message, vars);
  }
  return result;
}
